import { useEffect, useState } from 'react';
import {
  AI_PROVIDERS,
  aiProviderLabel,
  useAiKeys,
  useCreateAiKey,
  useDeleteAiKey,
} from '@wordpuppi/shared';
import type { AiProviderId } from '@wordpuppi/shared';
import { Link } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { confirmDialog } from '@/lib/dialog';

/**
 * Baxter's own key vault (AB#669). A personal key is the one thing you change
 * mid-chat — the paid model you wanted isn't in the picker because the key
 * isn't saved yet — so it belongs one cog away from the panel instead of three
 * clicks into /account/ai-keys.
 *
 * ponytail: PERSONAL keys only. Sharing a key with the account or pinning it to
 * a site needs the account/site pickers the full page already has, and this
 * links there rather than growing a second copy of them. Saving a key caches
 * that provider's model ids server-side, so the picker fills in on the next
 * `ai-models` refetch — nothing here has to know about models.
 */
/** Shared with the panel's inline banner so the two never drift (AB#669). */
export const FREE_EXHAUSTED_COPY =
  "You've used your 15 free actions on WordPuppi's key. Add your own free OpenRouter key and Baxter keeps working — the free models stay free, you just use your own quota.";
export const OPENROUTER_KEYS_URL = 'https://openrouter.ai/keys';
/** Fine-grained PAT with the "Copilot Requests" permission — what GitHub's own Copilot CLI takes. */
const COPILOT_PAT_URL = 'https://github.com/settings/personal-access-tokens/new';
const STRONG_MODEL_COPY =
  'A strong model builds a much better site. Add a key for Anthropic, OpenAI, or a paid OpenRouter model to generate.';

export function AiSettingsDialog({
  siteSlug,
  open,
  onOpenChange,
  nudge,
  defaultProvider,
}: {
  /** Omitted before a site exists (the New Site wizard opens this too). */
  siteSlug?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Why the dialog was opened for you rather than by you — extra copy above the form. */
  nudge?: 'free-exhausted' | 'strong-model';
  defaultProvider?: AiProviderId;
}) {
  const { data, isLoading } = useAiKeys(siteSlug);
  const keys = data?.data ?? [];
  const createKey = useCreateAiKey();
  const deleteKey = useDeleteAiKey();

  const [provider, setProvider] = useState<AiProviderId>(defaultProvider ?? 'openrouter');
  // The dialog stays mounted, so the caller's choice lands on OPEN, not on mount.
  useEffect(() => {
    if (open && defaultProvider) setProvider(defaultProvider);
  }, [open, defaultProvider]);
  const [label, setLabel] = useState('');
  const [key, setKey] = useState('');

  const save = async () => {
    const trimmedKey = key.trim();
    if (!trimmedKey) {
      toast.error('Paste a key');
      return;
    }
    try {
      await createKey.mutateAsync({
        provider,
        // The label is only ever a display name; default it rather than make
        // the quick path ask for one.
        label: label.trim() || aiProviderLabel(provider),
        key: trimmedKey,
        scope: 'user',
      });
      toast.success('Key added — its models are in the picker now');
      setLabel('');
      setKey('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add key');
    }
  };

  const remove = async (pid: string, name: string) => {
    if (
      !(await confirmDialog(
        `Delete "${name}"? Anything using it stops working immediately. This cannot be undone.`,
        { destructive: true, confirmLabel: 'Delete key' },
      ))
    )
      return;
    try {
      await deleteKey.mutateAsync(pid);
      toast.success('Key deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete key');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>AI settings</DialogTitle>
          <DialogDescription>
            Add your own provider key to unlock that provider's models in Baxter's picker.
            Without one, Baxter runs on the free OpenRouter tier.
          </DialogDescription>
        </DialogHeader>

        {nudge && (
          <p
            data-testid="ai-settings-nudge"
            className="rounded-lg border border-primary/40 bg-primary/5 p-3 text-sm"
          >
            {nudge === 'free-exhausted' ? FREE_EXHAUSTED_COPY : STRONG_MODEL_COPY}
          </p>
        )}

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ai-key-provider">Provider</Label>
            <Select value={provider} onValueChange={(v) => setProvider(v as AiProviderId)}>
              <SelectTrigger id="ai-key-provider" className="w-full">
                <SelectValue>{(v: unknown) => aiProviderLabel(String(v))}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {AI_PROVIDERS.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ai-key-value">API key</Label>
            <Input
              id="ai-key-value"
              type="password"
              autoComplete="off"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder={provider === 'copilot' ? 'github_pat_…' : 'sk-…'}
            />
            <p className="text-xs text-muted-foreground">
              {provider === 'copilot' ? (
                <>
                  A GitHub{' '}
                  <a href={COPILOT_PAT_URL} target="_blank" rel="noreferrer" className="underline">
                    fine-grained personal access token
                  </a>{' '}
                  with the <b>Copilot Requests</b> permission, on an account with an active Copilot
                  subscription. Models come from your Copilot plan.{' '}
                </>
              ) : null}
              Verified with the provider before it's stored, then encrypted. It is never shown
              again — only the last four characters.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ai-key-label">Name (optional)</Label>
            <Input
              id="ai-key-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={aiProviderLabel(provider)}
            />
          </div>

          <Button onClick={save} disabled={createKey.isPending || !key.trim()}>
            {createKey.isPending ? 'Verifying…' : 'Add key'}
          </Button>

          <div className="space-y-2 border-t pt-4">
            <p className="text-sm font-medium">Your keys</p>
            {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {!isLoading && keys.length === 0 && (
              <p className="text-sm text-muted-foreground">No keys yet.</p>
            )}
            {keys.map((k) => (
              <div key={k.pid} className="flex items-center gap-2 rounded-lg border p-2 text-sm">
                <span className="truncate font-medium">{k.label}</span>
                <Badge variant="secondary">{aiProviderLabel(k.provider)}</Badge>
                <span className="font-mono text-xs text-muted-foreground">••••{k.key_hint}</span>
                {/* Account- and site-scoped keys are managed on the full page. */}
                {k.scope === 'user' && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="ml-auto"
                    aria-label={`Delete ${k.label}`}
                    onClick={() => void remove(k.pid, k.label)}
                  >
                    <Trash2 className="text-muted-foreground" />
                  </Button>
                )}
              </div>
            ))}
            <Link
              to="/account/ai-keys"
              onClick={() => onOpenChange(false)}
              className="inline-block text-xs text-primary underline underline-offset-2"
            >
              Share a key with the account or pin it to one site →
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
